import api from './api';

// Tipos de questão disponíveis
export type QuestionType = 'multiple_choice' | 'true_false' | 'fill_blank' | 'essay';

export interface Quiz {
  _id: string;
  title: string;
  description?: string;
  type: 'deck-based' | 'custom';
  deckId?: string;
  deckTitle?: string;
  questions: QuizQuestion[];
  settings: QuizSettings;
  tags: string[];
  attemptsCount: number;
  lastAttempted?: string;
  averageScore: number;
  bestScore: number;
  isPublic: boolean;
  reviewCount: number;
  nextReview: string;
  easeFactor: number;
  interval: number;
  createdAt: string;
  updatedAt: string;
}

export interface QuizListItem {
  _id: string;
  title: string;
  description?: string;
  type: 'deck-based' | 'custom';
  deckId?: string;
  deckTitle?: string;
  questionsCount: number;
  tags: string[];
  attemptsCount: number;
  lastAttempted?: string;
  averageScore: number;
  bestScore: number;
  reviewCount: number;
  nextReview: string;
  easeFactor: number;
  interval: number;
  createdAt: string;
  updatedAt: string;
}

export interface QuizQuestion {
  id: string;
  question: string;
  questionType?: QuestionType;
  correctAnswer: string;
  wrongAnswers: string[];
  explanation?: string;
  cardId?: string;
  // Campos para preenchimento de lacunas
  blankText?: string;
  acceptableAnswers?: string[];
  // Campos para dissertativas
  expectedKeyPoints?: string[];
  minWords?: number;
  maxWords?: number;
  rubric?: string;
}

export interface QuizSettings {
  timeLimit?: number;
  randomizeQuestions: boolean;
  randomizeAnswers: boolean;
  showExplanations: boolean;
  allowRetries: boolean;
}

export interface CreateQuizData {
  title: string;
  description?: string;
  type: 'deck-based' | 'custom';
  deckId?: string;
  questionCount?: number;
  questions?: {
    question: string;
    questionType?: QuestionType;
    correctAnswer: string;
    wrongAnswers: string[];
    explanation?: string;
    // Fill-in-the-blank
    blankText?: string;
    acceptableAnswers?: string[];
    // Essay
    expectedKeyPoints?: string[];
    minWords?: number;
    maxWords?: number;
  }[];
  settings?: Partial<QuizSettings>;
  tags?: string[];
  isPublic?: boolean;
}

export interface UpdateQuizData {
  title?: string;
  description?: string;
  questions?: {
    question: string;
    questionType?: QuestionType;
    correctAnswer: string;
    wrongAnswers: string[];
    explanation?: string;
    blankText?: string;
    acceptableAnswers?: string[];
    expectedKeyPoints?: string[];
    minWords?: number;
    maxWords?: number;
  }[];
  settings?: Partial<QuizSettings>;
  tags?: string[];
  isPublic?: boolean;
}

export interface QuizAttempt {
  _id: string;
  quizId: string;
  userId: string;
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  timeSpent: number;
  isCompleted: boolean;
  startedAt: string;
  completedAt?: string;
  answers: {
    questionId: string;
    selectedAnswer: string;
    isCorrect: boolean;
    timeSpent?: number;
    questionType?: QuestionType;
    // Campos de avaliação por IA
    aiScore?: number;
    aiFeedback?: string;
    keyPointsMatched?: string[];
    keyPointsMissed?: string[];
  }[];
}

export interface QuizResults {
  attempt: QuizAttempt;
  quiz: {
    _id: string;
    title: string;
    description?: string;
    questions: {
      id: string;
      question: string;
      questionType?: QuestionType;
      correctAnswer: string;
      explanation?: string;
      blankText?: string;
      expectedKeyPoints?: string[];
    }[];
  };
  summary: {
    score: number;
    totalQuestions: number;
    correctAnswers: number;
    incorrectAnswers: number;
    timeSpent: number;
    isNewBestScore: boolean;
  };
}

export interface QuizStats {
  totalQuizzes: number;
  totalAttempts: number;
  averageScore: number;
  bestScore: number;
  totalTimeSpent: number;
  recentAttempts: QuizAttempt[];
}

class QuizService {
  async getQuizzes(): Promise<QuizListItem[]> {
    console.log('QuizService: Making API call to /quizzes');
    try {
      const response = await api.get('/quizzes');
      console.log('QuizService: API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('QuizService: API call failed:', error);
      throw error;
    }
  }

  async getQuiz(id: string): Promise<Quiz> {
    const response = await api.get(`/quizzes/${id}`);
    return response.data;
  }

  async createQuiz(quizData: CreateQuizData): Promise<Quiz> {
    const response = await api.post('/quizzes', quizData);
    return response.data;
  }

  async updateQuiz(id: string, quizData: UpdateQuizData): Promise<Quiz> {
    const response = await api.patch(`/quizzes/${id}`, quizData);
    return response.data;
  }

  async deleteQuiz(id: string): Promise<void> {
    await api.delete(`/quizzes/${id}`);
  }

  async startQuizAttempt(quizId: string): Promise<QuizAttempt> {
    const response = await api.post('/quizzes/attempts/start', { quizId });
    return response.data;
  }

  async submitQuizAttempt(
    attemptId: string,
    answers: {
      questionId: string;
      selectedAnswer: string;
      timeSpent?: number;
    }[],
    totalTimeSpent?: number
  ): Promise<QuizResults> {
    const response = await api.post('/quizzes/attempts/submit', {
      attemptId,
      answers,
      totalTimeSpent
    });
    return response.data;
  }

  async getQuizStats(): Promise<QuizStats> {
    const response = await api.get('/quizzes/stats');
    return response.data;
  }

  async getAttemptHistory(limit: number = 365): Promise<QuizAttempt[]> {
    const response = await api.get('/quizzes/attempts/history', {
      params: { limit }
    });
    return response.data;
  }

  async requestAIQuiz(aiRequest: {
    topic: string;
    description: string;
    questionCount: number;
    difficulty: 'beginner' | 'intermediate' | 'advanced';
    language: 'portuguese' | 'english' | 'spanish';
    additionalInfo?: string;
    questionTypes?: {
      multipleChoice?: number;
      trueFalse?: number;
      fillBlank?: number;
      essay?: number;
    };
    essayMinWords?: number;
  }): Promise<{ success: boolean; message: string; quizId?: string }> {
    const response = await api.post('/quizzes/ai/generate', aiRequest);
    return response.data;
  }

  async createQuizFromFile(formData: FormData): Promise<{ success: boolean; message: string; quizId: string }> {
    const response = await api.post('/quizzes/ai/generate-from-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

export default new QuizService();